import { ChangeEvent, FormEvent, useState } from 'react';

import { Button, Dialog, DialogContent, Stack, TextField, Typography } from '@mui/material';

import kebabCase from 'lodash/kebabCase';

import DialogTitleWithX from '@/components/DialogTitleWithX';
import { DialogProps } from '@toolpad/core/useDialogs';

import { DialogActions } from '../styled';
import { SafeguardConfirmationDialogPayload } from './types';

function SafeguardConfirmationDialog({
  payload: {
    title,
    prompt,
    validationText,
    dialogId = kebabCase(title),
    okColor = 'error',
    okText = 'Confirm',
    validationHint = '',
    cancelText = 'Cancel',
    autoFocus = true,
    width = 'fit-content',
  },
  open,
  onClose,
}: DialogProps<SafeguardConfirmationDialogPayload, boolean>) {
  const [validation, setValidation] = useState('');
  const validationMatched = validation?.toLowerCase() === validationText.toLowerCase();

  function handleClose(confirmed: boolean) {
    onClose(confirmed);
  }

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    handleClose(true);
  }

  function handleChange(ev: ChangeEvent<HTMLInputElement>) {
    setValidation(ev.target.value);
  }

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      aria-labelledby={`${dialogId}-title`}
      aria-describedby={`${dialogId}-description`}
      slotProps={{
        paper: {
          sx: { width, maxWidth: 800 },
        },
      }}
    >
      <DialogTitleWithX onClose={() => handleClose(false)}>{title}</DialogTitleWithX>
      <DialogContent sx={{ mt: 2 }}>
        <Stack gap={2}>
          <Typography component="div" whiteSpace="pre-line" variant="body1">
            {prompt}
          </Typography>

          <Stack gap={1}>
            <Typography>
              Please type &apos;
              <Typography variant="h5" component="span">
                {validationText}
              </Typography>
              &apos; to confirm:
            </Typography>
            <TextField
              variant="outlined"
              autoFocus={autoFocus}
              fullWidth
              value={validation}
              onChange={handleChange}
              placeholder={validationHint}
              data-cy="safeguard-confirm-input"
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => handleClose(false)}
          data-cy={`${dialogId}-cancel`}
        >
          {cancelText}
        </Button>
        <Button
          type="submit"
          variant="contained"
          color={okColor}
          disabled={!validationMatched}
          onClick={handleSubmit}
          data-cy={`${dialogId}-ok`}
        >
          {okText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SafeguardConfirmationDialog;
